/**
 * Utility functions for RegenerateDropdown component.
 * Extracted for testability and reusability.
 */

import { sectionHeaders } from '../editors/spec/blocks/blockDefinitions.js';

// Extended section definitions with subsections
export const sectionSubsections = {
    'summary': [
        {
            id: 'summary-overview',
            title: 'Brief Summary - Overview',
            context: 'overview',
            regenerative: true
        },
        {
            id: 'summary-literal-support',
            title: 'Brief Summary - Literal Support',
            context: 'summary',
            regenerative: true
        }
    ],
    'detailed': [
        {
            id: 'detailed-description-regenerative',
            title: 'Detailed Description - Regenerative Block',
            context: 'detailed-description',
            regenerative: true
        }
    ]
};

// Helper to get all subsections as flat array
export const getAllSubsections = () => Object.values(sectionSubsections).flat();

// Sections that support regenerative content
export const regenerativeSections = new Set([
    'title', 'field', 'background', 'summary-overview',
    'summary-literal-support', 'embodiments',
    'detailed-description-regenerative', 'abstract'
]);

/**
 * Build section groups dynamically from sectionHeaders (single source of truth)
 * @returns {Object} Object with 'Common Sections' and 'Additional Sections' arrays
 */
export function buildSectionGroups() {
    const commonSections = [];
    const additionalSections = [];

    for (const section of sectionHeaders) {
        // Check if this section has subsections
        const subsections = sectionSubsections[section.id];

        if (subsections) {
            // Replace parent section with its subsections
            const targetList = section.defaultEnabled ? commonSections : additionalSections;
            subsections.forEach(sub => targetList.push(sub.id));
        } else {
            // Add the section itself
            if (section.defaultEnabled) {
                commonSections.push(section.id);
            } else {
                additionalSections.push(section.id);
            }
        }
    }

    return {
        'Common Sections': commonSections,
        'Additional Sections': additionalSections
    };
}

/**
 * Get default selected sections based on defaultEnabled from sectionHeaders
 * @returns {Set} Set of default section IDs
 */
export function getDefaultSelectedSections() {
    const selected = new Set();

    for (const section of sectionHeaders) {
        if (section.defaultEnabled) {
            const subsections = sectionSubsections[section.id];
            if (subsections) {
                // Add all subsections for this parent
                subsections.forEach(sub => selected.add(sub.id));
            } else {
                selected.add(section.id);
            }
        }
    }

    return selected;
}

/**
 * Extract section types that exist in the document from HTML content
 * @param {string} htmlContent - The HTML content of the document
 * @returns {Set} Set of section IDs found in the document
 */
export function getSectionsInDocument(htmlContent) {
    if (!htmlContent) return new Set();

    const sectionsInDoc = new Set();
    // Match data-section-type="sectionId" in the HTML
    const regex = /data-section-type="([^"]+)"/g;
    let match;
    while ((match = regex.exec(htmlContent)) !== null) {
        sectionsInDoc.add(match[1]);
    }
    return sectionsInDoc;
}

/**
 * Sync selected sections with what exists in the document
 * @param {string} htmlContent - The HTML content of the document
 * @param {function} getDefaultsFn - Function to get default selections
 * @returns {Set} Updated set of selected sections
 */
export function syncWithDocument(htmlContent, getDefaultsFn = getDefaultSelectedSections) {
    const sectionsInDoc = getSectionsInDocument(htmlContent);

    // If document is empty, use defaults
    if (sectionsInDoc.size === 0) {
        return getDefaultsFn();
    }

    const newSelected = new Set();

    // For each section in the document, add it (or its subsections) to selected
    for (const sectionId of sectionsInDoc) {
        const subsections = sectionSubsections[sectionId];
        if (subsections) {
            // Add all subsections for this parent section
            subsections.forEach(sub => newSelected.add(sub.id));
        } else {
            // Add the section itself
            newSelected.add(sectionId);
        }
    }

    return newSelected;
}

/**
 * Get all available section IDs (main sections + subsections)
 * @returns {Array} Array of all section IDs
 */
export function getAllAvailableSectionIds() {
    const mainSectionIds = sectionHeaders.map(s => s.id);
    const subsectionIds = getAllSubsections().map(sub => sub.id);
    return [...mainSectionIds, ...subsectionIds];
}

/**
 * Find parent section for any subsection
 * @param {string} subsectionId - The subsection ID to look up
 * @returns {string|null} The parent section ID or null if not a subsection
 */
export function getParentSection(subsectionId) {
    for (const [parentId, subsections] of Object.entries(sectionSubsections)) {
        if (subsections.some(sub => sub.id === subsectionId)) {
            return parentId;
        }
    }
    return null;
}

/**
 * Get section by ID (checks subsections first, then main sections)
 * @param {string} id - The section ID to look up
 * @returns {Object|undefined} The section object or undefined
 */
export function getSectionById(id) {
    // Check subsections first
    const allSubsections = getAllSubsections();
    const subsection = allSubsections.find(sub => sub.id === id);
    if (subsection) {
        return subsection;
    }
    // Then check main sections
    return sectionHeaders.find(s => s.id === id);
}

/**
 * Check if a section is regenerative
 * @param {string} id - The section ID to check
 * @returns {boolean} True if the section supports regenerative content
 */
export function isRegenerativeSection(id) {
    // Check if it's a standard regenerative section
    if (regenerativeSections.has(id)) {
        return true;
    }
    // Check if it's a regenerative subsection
    const allSubsections = getAllSubsections();
    const subsection = allSubsections.find(sub => sub.id === id);
    if (subsection && subsection.regenerative) {
        return true;
    }
    return false;
}
